import { z } from "zod";
import { id } from "./common";

export const messageKind = z.enum(["manual", "drip", "reminder", "damage", "insurance", "contract", "payment", "tour", "system", "staff_alert"]);
export type MessageKind = z.infer<typeof messageKind>;
export const callOutcome = z.enum(["no_answer", "voicemail", "spoke"]);
export type CallOutcome = z.infer<typeof callOutcome>;

const target = { contactId: id.optional(), leadId: id.optional(), bookingId: id.optional() };
const needsTarget = (t: { contactId?: string; leadId?: string; bookingId?: string }) => Boolean(t.contactId || t.leadId || t.bookingId);
const smsBody = z.string().trim().min(1).max(1600);
const emailBody = z.string().trim().min(1).max(20000);
const emailSubject = z.string().trim().min(1).max(200);

export const smsSend = z.object({ ...target, to: z.string().trim().max(40).optional(), body: smsBody }).refine(needsTarget, { message: "contactId, leadId or bookingId is required", path: ["contactId"] });
export type SmsSend = z.infer<typeof smsSend>;

export const emailSend = z
  .object({ ...target, to: z.string().trim().max(200).optional(), subject: emailSubject, body: emailBody })
  .refine(needsTarget, { message: "contactId, leadId or bookingId is required", path: ["contactId"] });
export type EmailSend = z.infer<typeof emailSend>;

export const callLog = z.object({ ...target, outcome: callOutcome, notes: z.string().trim().max(2000).optional() }).refine(needsTarget, { message: "contactId, leadId or bookingId is required", path: ["contactId"] });
export type CallLog = z.infer<typeof callLog>;

/** Internal automation permits a staff recipient without a contact. Public
 * manual commands retain their existing needsTarget gate above. */
const sendFields = { companyId: id, contactId: id.nullable().optional(), leadId: id.nullable().optional(), bookingId: id.nullable().optional(), kind: messageKind, templateKey: z.string().nullable().optional(), dedupeKey: z.string().nullable().optional(), dripEnrollmentId: id.nullable().optional() };
export const smsCommand = z.object({ ...sendFields, to: z.string().trim().max(40).nullable().optional(), body: smsBody });
export const emailCommand = z.object({ ...sendFields, to: z.string().trim().max(200).nullable().optional(), subject: emailSubject, body: emailBody });
export const messageRecord = z.object({
  ...sendFields,
  companyId: id.optional(), kind: messageKind,
  channel: z.enum(["sms", "email", "call"]), direction: z.enum(["in", "out"]),
  body: z.string(), subject: z.string().nullable().optional(),
  status: z.enum(["held", "queued", "sending", "sent", "delivered", "failed", "bounced", "cancelled", "received", "logged"]).optional(),
});

export const conversationsQuery = z.object({ q: z.string().trim().default(""), limit: z.coerce.number().int().min(1).max(100).default(30) });
export const quickRepliesQuery = z.object({ leadId: id.optional() });
